/**
 * Utility to help generate unique namespaces for translation modules.
 *
 * Automatically extracts namespace from file path using import.meta.url,
 * ensuring uniqueness without manual configuration.
 *
 * @example
 * // In src/layouts/app-layout/nav-main/locales/index.ts
 * import { defineTranslations } from '@/hooks/use-component-translation/utils'
 * import enUs from './enUs.json'
 * import ptBr from './ptBr.json'
 *
 * export default defineTranslations(import.meta.url, {
 *   enUs,
 *   ptBr,
 * })
 *
 * // Namespace will automatically be: 'layouts/app-layout/nav-main'
 */

import i18n from '@/i18n'
import { logger } from '@/lib/logger'

import type { TranslationModule } from './use-component-translation'

type TranslationResources = Record<string, Record<string, any>>

/**
 * Eagerly registers a namespace's resources with i18next at module-load time.
 * Doing this at import time (before any component renders) is what keeps
 * `useComponentTranslation` from triggering i18next's "added" listeners
 * mid-render — which would otherwise queue setState in unrelated translated
 * components and trip React's "setState during render" warning.
 */
function registerTranslations(
  namespace: string,
  translations: TranslationResources,
): void {
  for (const [locale, resources] of Object.entries(translations)) {
    if (!i18n.hasResourceBundle(locale, namespace)) {
      i18n.addResourceBundle(locale, namespace, resources)
    }
  }
}

/**
 * Extracts the namespace from a file path.
 * Converts something like:
 *   - 'file:///path/to/project/src/layouts/app-layout/nav-main/locales/index.ts'
 * Into:
 *   - 'layouts/app-layout/nav-main'
 *
 * @param fileUrl - The import.meta.url of the locales/index.ts file
 * @returns The namespace derived from the file path
 */
function extractNamespaceFromUrl(fileUrl: string): string {
  try {
    // Remove protocol prefixes (file://, http://, https://, etc.)
    let filePath = fileUrl.replace(/^[a-z]+:\/\//, '')

    // Normalize path separators (in case of Windows)
    filePath = filePath.replace(/\\/g, '/')

    // Try to find the 'src/' part and extract everything after it
    const srcIndex = filePath.lastIndexOf('/src/')
    let pathAfterSrc: string

    if (srcIndex !== -1) {
      // Development mode or path contains '/src/'
      pathAfterSrc = filePath.substring(srcIndex + 5) // +5 for '/src/'
    } else {
      // Production/build mode: path might not contain '/src/'
      // In Cloudflare and other production environments, the path structure changes
      // We'll try to extract a meaningful namespace from whatever path we have

      // Remove query parameters and hash
      filePath = filePath.split('?')[0].split('#')[0]

      // Remove common build artifacts patterns
      // e.g., /assets/index-a1b2c3.js -> index
      filePath = filePath.replace(/\/assets\//, '/')
      filePath = filePath.replace(/-[a-f0-9]{8}\./g, '.')

      // If path starts with /, remove it
      if (filePath.startsWith('/')) {
        filePath = filePath.substring(1)
      }

      pathAfterSrc = filePath
    }

    // Remove '/locales/index.ts' or '/locales/index.js' or similar from the end
    pathAfterSrc = pathAfterSrc.replace(
      /\/locales\/index\.(ts|js|tsx|jsx)$/,
      '',
    )

    // Remove file extensions
    pathAfterSrc = pathAfterSrc.replace(/\.(js|ts|tsx|jsx)$/, '')

    // If we still don't have a valid path, throw an error
    if (!pathAfterSrc || pathAfterSrc.trim() === '') {
      throw new Error('Could not extract a valid namespace from the file path')
    }

    return pathAfterSrc
  } catch (error) {
    logger.error(
      'Failed to extract namespace from URL',
      error instanceof Error ? error : undefined,
      { fileUrl },
      'i18n',
    )
    throw new Error(
      'Failed to automatically extract namespace. Please provide it manually using createTranslationModule({ path: "your/path", translations })',
    )
  }
}

/**
 * Defines translations with automatic namespace detection.
 *
 * This is the recommended way to create translation modules as it eliminates
 * the possibility of namespace collisions by automatically using the file path.
 *
 * @param fileUrl - Pass import.meta.url here
 * @param translations - Object with translation resources (enUs, ptBr, etc.)
 * @returns TranslationModule with auto-generated namespace
 *
 * @example
 * // In src/pages/login/locales/index.ts
 * export default defineTranslations(import.meta.url, {
 *   enUs,
 *   ptBr,
 * })
 */
export function defineTranslations(
  fileUrl: string,
  translations: TranslationResources,
): TranslationModule {
  const namespace = extractNamespaceFromUrl(fileUrl)

  if (process.env.NODE_ENV === 'development') {
    const validation = validateNamespace(namespace)
    if (!validation.valid) {
      logger.warning(
        `Generated namespace "${namespace}" validation warning: ${validation.warning}`,
        { namespace, warning: validation.warning },
        'i18n',
      )
    }
  }

  // Register at import time so the bundle is in place before any component
  // calls `useComponentTranslation` — avoids the i18next "added" emit firing
  // mid-render and queueing setState in already-rendered translated siblings.
  registerTranslations(namespace, translations)

  return {
    namespace,
    translations,
  }
}

/**
 * Creates a translation module with a manually specified namespace.
 *
 * Use this when you need explicit control over the namespace,
 * or when import.meta.url is not available.
 *
 * @example
 * export default createTranslationModule({
 *   path: 'layouts/app-layout/nav-main',
 *   translations: { enUs, ptBr }
 * })
 */
export function createTranslationModule(options: {
  path: string
  translations: TranslationResources
}): TranslationModule {
  const { path, translations } = options

  if (!path) {
    throw new Error(
      '[createTranslationModule] Path is required. ' +
        'Please provide the full path relative to src/, e.g., "layouts/app-layout/nav-main"',
    )
  }

  const validation = validateNamespace(path)
  if (!validation.valid && process.env.NODE_ENV === 'development') {
    logger.warning(
      `Namespace "${path}" validation warning: ${validation.warning}`,
      { namespace: path, warning: validation.warning },
      'i18n',
    )
  }

  // Same eager registration as defineTranslations — keeps the hook from
  // emitting i18next "added" events mid-render.
  registerTranslations(path, translations)

  return {
    namespace: path,
    translations,
  }
} /**
 * Validates if a namespace follows the recommended pattern.
 * Returns true if valid, false otherwise with a warning message.
 */
export function validateNamespace(namespace: string): {
  valid: boolean
  warning?: string
} {
  if (!namespace || namespace.trim() === '') {
    return {
      valid: false,
      warning: 'Namespace cannot be empty',
    }
  }

  // Check if it contains a path separator (/)
  if (!namespace.includes('/')) {
    return {
      valid: false,
      warning: `Namespace "${namespace}" should use full path format (e.g., "layouts/app-layout/${namespace}") to avoid collisions`,
    }
  }

  // Check for common anti-patterns
  const antiPatterns = [
    { pattern: /^\//, message: 'Namespace should not start with /' },
    { pattern: /\/$/, message: 'Namespace should not end with /' },
    { pattern: /\/\//, message: 'Namespace should not contain double slashes' },
    { pattern: /\s/, message: 'Namespace should not contain spaces' },
  ]

  for (const { pattern, message } of antiPatterns) {
    if (pattern.test(namespace)) {
      return {
        valid: false,
        warning: message,
      }
    }
  }

  return { valid: true }
}
